import * as Location from 'expo-location';
import { useCallback } from 'react';
import { Linking } from 'react-native';

import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';

import { captureException } from '@/libs/logger';
import { useUiStore } from '@/stores';
import { useShallow } from 'zustand/shallow';
import { geocodeAddress } from './geocode-address';

export type Coords = { latitude: number; longitude: number };

/**
 * Resolve the first live fix via a short-lived watch subscription, or `null` after
 * `timeoutMs`. Used as a fallback when the one-shot `getCurrentPositionAsync` rejects with
 * `kCLErrorLocationUnknown` (kCLErrorDomain error 0) — a transient iOS error right after the
 * permission grant that means "no fix yet, keep listening". A watch keeps CoreLocation running
 * and delivers the position as soon as it arrives, which the one-shot call gives up on. Uses
 * coarse (network/cell) accuracy so it can still resolve indoors, where a precise GPS fix
 * can never lock.
 */
const waitForFirstFix = (timeoutMs = 3000): Promise<Coords | null> =>
  new Promise(resolve => {
    let subscription: Location.LocationSubscription | null = null;
    let settled = false;
    const finish = (coords: Coords | null) => {
      if (settled) return;
      settled = true;
      subscription?.remove();
      resolve(coords);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    Location.watchPositionAsync(
      { accuracy: Location.Accuracy.Lowest, timeInterval: 0, distanceInterval: 0 },
      position => {
        clearTimeout(timer);
        finish({ latitude: position.coords.latitude, longitude: position.coords.longitude });
      },
    )
      .then(sub => {
        subscription = sub;
        // Timed out before the subscription handle resolved — tear it straight back down.
        if (settled) sub.remove();
      })
      .catch(() => {
        clearTimeout(timer);
        finish(null);
      });
  });

/** Minimum gap between position updates pushed from `watch`. Movement (walking/driving)
 *  fires GPS ticks far more often than we want to recompute the list, so we batch them to
 *  at most one update per this window. */
const WATCH_INTERVAL_MS = 6000;

/** iOS `kCLErrorLocationUnknown` (kCLErrorDomain error 0): a transient "no fix yet, keep
 *  listening" that the watch fallback recovers from — not worth reporting to Sentry. */
const isLocationUnknownError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return /kCLErrorDomain error 0|Cannot obtain current location/i.test(message);
};

/**
 * Foreground ("when in use") location permission + current position (one-shot `request`
 * or a batched `watch` that keeps firing as the guardian moves).
 *
 * UX contract (see the find-academy onboarding step):
 *  - Never prompts on app launch — call `request()` at the moment the location is
 *    actually needed (entering the map / tapping "use my location").
 *  - Asks the OS only while it still can (status `undetermined`, or `denied` but
 *    `canAskAgain`). Once the user has blocked it for good we stop nagging and instead
 *    surface an alert that routes them to Settings — mirroring the photo-permission
 *    flow in `use-avatar-picker`.
 *  - Returns `null` on any non-granted outcome so callers fall back to a default region
 *    rather than blocking the screen.
 */
export function useCurrentLocation() {
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));
  const request = useCallback(async (): Promise<Coords | null> => {
    // Show the global loading through the whole primary detection (permission + last-known +
    // one-shot fix), so the guardian gets feedback while we work out where they are. The watch
    // fallback below runs UNBLOCKED — it can take a while (or never resolve indoors).
    showGlobalLoading();
    try {
      const current = await Location.getForegroundPermissionsAsync();

      let status = current.status;
      if (status === 'undetermined' || (status === 'denied' && current.canAskAgain)) {
        ({ status } = await Location.requestForegroundPermissionsAsync());
      }
      if (status !== 'granted') {
        // Blocked for good (denied and the OS won't show its dialog again) → guide to
        // Settings. While the user can still be asked we stay silent and just fall back.
        if (status === 'denied' && !current?.canAskAgain) {
          showAlert({
            title: STRINGS.common.locationPermissionAlert.title,
            message: STRINGS.common.locationPermissionAlert.message,
            buttons: [
              {
                text: STRINGS.common.locationPermissionAlert.settings,
                onPress: () => Linking.openSettings(),
              },
              { text: STRINGS.common.locationPermissionAlert.notNow, style: 'default' },
            ],
          });
        }
        return null;
      }

      // Prefer the OS's last-known fix so the map can recentre *immediately*. A fresh fix on a
      // real device is a cold GPS start that can take several seconds (on the simulator it's
      // instant, which is why the lag only shows on device) — during which the map would sit on
      // the default region. `watch()` then refines to the live position as updates arrive.
      try {
        const cached = await Location.getLastKnownPositionAsync();
        if (cached) {
          return { latitude: cached.coords.latitude, longitude: cached.coords.longitude };
        }
      } catch {
        // No cached fix available — fall through to a live one.
      }

      const position = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Balanced,
      });
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
    } catch (error) {
      // getCurrentPositionAsync routinely rejects the FIRST one-shot on iOS with
      // kCLErrorLocationUnknown (error 0) right after the grant — GPS has no fix yet. Report
      // only genuine permission failures (not that transient one), then fall through.
      if (!isLocationUnknownError(error)) {
        captureException(
          { error_payload: error },
          { tags: { usk_log_type: 'request_location_error' } },
        );
      }
    } finally {
      hideGlobalLoading();
    }

    // Unblocked fallback: a live watch resolves on the first emission instead of giving up like
    // the one-shot did. We don't hold the global loading on it — it can take a while.
    return waitForFirstFix();
  }, [hideGlobalLoading, showGlobalLoading]);

  /**
   * Subscribe to position changes, batched to one update per {@link WATCH_INTERVAL_MS}.
   * Assumes permission is already granted (call `request()` first); resolves to `null` if
   * it isn't. The returned subscription MUST be `.remove()`d on cleanup.
   */
  const watch = useCallback(
    async (onChange: (coords: Coords) => void): Promise<Location.LocationSubscription | null> => {
      const { status } = await Location.getForegroundPermissionsAsync();
      if (status !== 'granted') return null;

      // Android honours `timeInterval`; iOS ignores it and emits on distance, so we also
      // guard with a leading-edge throttle to hold every platform to one update per window.
      let lastPushedAt = 0;
      return Location.watchPositionAsync(
        {
          accuracy: Location.Accuracy.Balanced,
          timeInterval: WATCH_INTERVAL_MS,
          distanceInterval: 10,
        },
        position => {
          const now = Date.now();
          if (now - lastPushedAt < WATCH_INTERVAL_MS) return;
          lastPushedAt = now;
          onChange({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
      );
    },
    [],
  );

  /**
   * Forward-geocode a free-form address / city / zip code into coordinates. No location
   * permission is needed — this only resolves an address, it doesn't read the device
   * position. Platform-split: native uses the on-device geocoder, web uses the Maps JS
   * Geocoder (see `geocode-address[.web].ts`).
   */
  const geocode = useCallback((address: string): Promise<Coords | null> => {
    return geocodeAddress(address);
  }, []);

  return { request, watch, geocode };
}
